import React, { useEffect, useState } from "react";
import MyPagination from "../Common/MyPagination";
import ImportContactsModal, {
  ImportedContactFileInfoType,
  ImportedRecords,
} from "../Broadcast/ImportContactsModal";
import {
  useDeleteTempFilesMutation,
  useLazyGetTempFilesQuery,
} from "../store/slices/contactsSlice";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";
import { getImportedFileName } from "../helpers";
import EmailIcon from "../../assets/icons/icon_email-01.svg2";
import MessageIcon from "../../assets/icons/messages.svg2";
import VoiceIcon from "../../assets/icons/icon_voice_tel-01.svg2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import MyModal from "../Common/MyModal";
import { ImportFileAsContacts } from "../Broadcast/ImportFileAsContacts";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";

const { countries } = JSON.parse(
  (document.querySelector("#app") as any)?.dataset?.page
).props;

export default function ImportedContactFilesList({
  actionButton,
  selectedFileName,
  setFile = () => {},
  headers,
  filters,
  showViewRecordsButton,
  showSaveContactsButton,
}: {
  selectedFileName?: string;
  actionButton?: (f: ImportedContactFileInfoType) => React.JSX.Element;
  setFile?: (f: ImportedContactFileInfoType | undefined) => void;
  headers?: string[];
  filters?: {
    status?: string;
    search?: string;
  };
  showViewRecordsButton?: boolean;
  showSaveContactsButton?: boolean;
}) {
  const [totalResults, setTotalResults] = useState(18);
  const [availableTemplates, setAvailableTemplates] = useState<
    ImportedContactFileInfoType[]
  >([]);

  const [orderBy, setOrderBy] = useState("created");
  const [order, setOrder] = useState("desc");

  const confirmDialog = useConfirmDialog() as
    | ((title: string, message: string) => Promise<boolean>)
    | null;
  const [fetchTempFiles] = useLazyGetTempFilesQuery();
  const templates = Array.isArray(availableTemplates) ? availableTemplates : [];
  const [deleteTempFiles] = useDeleteTempFilesMutation();

  const handleDelete = async (uuid: string) => {
    if (!confirmDialog) return;
    if (
      !(await confirmDialog(
        "Delete file",
        "Are you sure you want to delete this imported file?"
      ))
    )
      return;
    try {
      await deleteTempFiles([uuid]).unwrap();
      loader();
    } catch {
      // silently ignore
    }
  };

  const [loading, setLoading] = useState(false);
  const loader = async (page = 0, perPage = 10) => {
    setLoading(true);
    try {
      const data = await fetchTempFiles({
        search: filters?.search || "",
        orderBy,
        order,
        status: filters?.status || "",
        page,
        perPage,
        mustHeaders: headers || [],
      }).unwrap();
      if (data) {
        setAvailableTemplates(data.files || []);
        setTotalResults(data.total);
      }
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const timeOut = setTimeout(() => {
      loader();
    }, 500);

    return () => {
      clearTimeout(timeOut);
    };
  }, [orderBy, order, headers, filters?.search, filters?.status]);

  const sortArrow = (
    <span className="ms-2">
      {order === "asc" ? (
        <FontAwesomeIcon icon={faAngleUp} />
      ) : (
        <FontAwesomeIcon icon={faAngleDown} />
      )}
    </span>
  );

  return (
    <div className="mb-5">
      <FullScreenLoadingIndicator loading={loading} />
      <div className="p-3 bg-white rounded-3">
        {templates.length === 0 ? (
          <div className="text-center py-5 text-muted">
            There are no imported files to show
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table mb-5">
              <thead>
                <tr>
                  <th>File name</th>
                  <th>Created by</th>
                  <th>
                    <button
                      className={`btn p-0 fw-bold ${
                        orderBy === "created" ? "text-decoration-underline" : ""
                      } `}
                      onClick={() => {
                        setOrderBy("created");
                        setOrder(
                          orderBy === "created" && order === "desc"
                            ? "asc"
                            : "desc"
                        );
                      }}
                    >
                      Created at
                    </button>
                    {orderBy === "created" && sortArrow}
                  </th>
                  <th>Matched field</th>
                  <th>Processed fields</th>
                  <th>
                    <button
                      className={`btn p-0 fw-bold ${
                        orderBy === "access" ? "text-decoration-underline" : ""
                      } `}
                      onClick={() => {
                        setOrderBy("access");
                        setOrder(
                          orderBy === "access" && order === "desc"
                            ? "asc"
                            : "desc"
                        );
                      }}
                    >
                      Last used
                    </button>
                    {orderBy === "access" && sortArrow}
                  </th>
                  <th>Stats</th>
                  <th>Rows </th>
                  <th>Default country</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr
                    key={template.file}
                    className={
                      selectedFileName === template.file ? "table-primary" : ""
                    }
                  >
                    <td className="text-nowrap">
                      {getImportedFileName(template.file)}
                    </td>
                    <td>{template.createdBy}</td>
                    <td>{new Date(template.createdAt).toLocaleString()}</td>
                    <td>{template.matchBy}</td>
                    <td>{template.header.join(", ")}</td>
                    <td>{template.lastAccessAt}</td>
                    <td>
                      {[
                        { field: "email", icon: EmailIcon },
                        { field: "sms", icon: MessageIcon },
                        { field: "voice", icon: VoiceIcon },
                      ].map((s) => (
                        <span
                          key={s.field}
                          className="badge badge-sm bg-icon me-2 mb-1"
                        >
                          <s.icon width={15} className="me-2" />
                          <span className="text-purple">
                            {(template.stats as any)[s.field]}
                          </span>
                        </span>
                      ))}

                      <span
                        className="badge badge-sm bg-icon text-purple"
                        style={{ padding: 6 }}
                      >
                        TOTAL: {template.stats.contacts}
                      </span>
                    </td>
                    <td>{template.stats.rows}</td>
                    <td>
                      {
                        countries.find(
                          (c: { country_code: string; country_name: string }) =>
                            c.country_code === template.defaultCountryIso
                        ).country_name
                      }
                    </td>
                    <td>
                      <div className="d-flex just gap-2 align-items-center flex-nowrap text-nowrap justify-content-end">
                        {actionButton?.(template)}

                        {showViewRecordsButton && (
                          <MyModal
                            label={
                              <span className="btn bg-colored text-white">
                                View records
                              </span>
                            }
                            heading={"File contents"}
                            size="xl"
                            buttonClassName="btn-link"
                          >
                            <ImportedRecords importedFileId={template.file} />
                          </MyModal>
                        )}

                        {showSaveContactsButton && (
                          <MyModal
                            label={
                              <span className="btn bg-colored text-white">
                                Import contacts
                              </span>
                            }
                            heading={"Import contacts from this file"}
                            size="xxl"
                            buttonClassName="btn-link"
                          >
                            <ImportFileAsContacts
                              importedFileId={template.file}
                            />
                          </MyModal>
                        )}

                        <button
                          className="btn btn-colored text-white"
                          onClick={() => handleDelete(template.uuid)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="my-5">
          <MyPagination total={totalResults} onPaginate={loader} />
        </div>
      </div>
    </div>
  );
}
